import React from "react";
import { CheckIcon, MinusIcon } from "lucide-react";
import { useSeo, faqSchema } from "../hooks/useSeo";
import { PageHero } from "../components/ui/PageHero";
import { CtaBand } from "../components/ui/CtaBand";
import { Reveal } from "../components/ui/Reveal";
import { SectionHeading } from "../components/ui/SectionHeading";
import { FaqAccordion } from "../components/ui/FaqAccordion";
import { PlaceholderTag } from "../components/ui/PlaceholderTag";
import { PricingCards } from "../components/pricing/PricingCards";
import { ADDONS, COMPARISON } from "../data/pricing";
import { PRICING_FAQS } from "../data/faqs";
import { PRICING } from "../data/site";
const schema = faqSchema(PRICING_FAQS);
function Mark({
  on
}: {
  on: boolean;
}) {
  return on ? <CheckIcon className="mx-auto h-5 w-5 text-accent-soft" aria-label="Included" data-id="element-773" data-mp="3ccasmalhzuzf" /> : <MinusIcon className="mx-auto h-5 w-5 text-muted/60" aria-label="Not included" data-id="element-774" data-mp="1ffk1fvd33rqm" />;
}
export function Pricing() {
  useSeo({
    title: "Pricing",
    description: "Website Development $600 one time. Hosting & Maintenance $75/month. Cancel anytime.",
    path: "/pricing",
    schema
  });
  return <>
      <PageHero crumbs={[{
      label: "Pricing"
    }]} eyebrow="Pricing" title="Simple, fixed pricing." lead="A one-time website build, and optional monthly hosting & maintenance. Your scope is confirmed in writing before any work begins." data-id="element-775" data-mp="14nsbnfx2m8r3" />
      <section className="shell section" data-id="element-776" data-mp="1xd7topn5l1wi">
        <PricingCards source="pricing" data-id="element-777" data-mp="22c2e1w0rqyiq" />
      </section>

      <section className="shell section border-t border-line" data-id="element-778" data-mp="22dlubmmfd9ki">
        <SectionHeading eyebrow="Compare" title="What's included." data-id="element-779" data-mp="1nllfqljfkoq2" />
        <Reveal className="overflow-x-auto rounded-[clamp(1rem,1.2vw,1.5rem)] border border-line" data-id="element-780" data-mp="1slg52knk5690">
          <table className="w-full min-w-[560px] border-collapse text-left" data-id="element-781" data-mp="2gbk6cld9wtwu">
            <caption className="sr-only" data-id="element-782" data-mp="2g9h6l9mbjmex">Comparison of Website Development and Hosting & Maintenance</caption>
            <thead className="bg-ink-2" data-id="element-783" data-mp="2uy0ooj4ddfm8">
              <tr data-id="element-784" data-mp="2uwpo0s680nzc">
                <th scope="col" className="p-5 font-medium text-muted" data-id="element-785" data-mp="2ux7tmferwejx">Feature</th>
                <th scope="col" className="w-[24%] p-5 text-center font-display text-lg font-semibold" data-id="element-786" data-mp="2q07ktwqjgxp4">
                  {PRICING.build.name}
                  <span className="block font-sans text-sm font-normal text-muted" data-id="element-787" data-mp="2gy84l6nclxn8">{PRICING.build.price} {PRICING.build.cadence}</span>
                </th>
                <th scope="col" className="w-[24%] p-5 text-center font-display text-lg font-semibold" data-id="element-788" data-mp="2bzwtytp8bj3y">
                  {PRICING.hosting.name}
                  <span className="block font-sans text-sm font-normal text-muted" data-id="element-789" data-mp="229bn6izej89y">{PRICING.hosting.price}{PRICING.hosting.cadence}</span>
                </th>
              </tr>
            </thead>
            <tbody data-id="element-790" data-mp="1y5x8pwr42zym">
              {COMPARISON.map(row => <tr key={row.feature} className="border-t border-line" data-id="element-791" data-mp="1t842x4tq07yv">
                  <th scope="row" className="p-5 font-normal text-fg-2" data-id="element-792" data-mp="1o9apfcefogyp">{row.feature}</th>
                  <td className="p-5" data-id="element-793" data-mp="3s1j9ttilnhd"><Mark on={row.build} data-id="element-794" data-mp="3qgoc33ag2iz" /></td>
                  <td className="p-5" data-id="element-795" data-mp="3tvzwvxr6tfn"><Mark on={row.hosting} data-id="element-796" data-mp="8qcqdwsfdjvw" /></td>
                </tr>)}
            </tbody>
          </table>
        </Reveal>
      </section>

      <section className="border-y border-line bg-ink-2" data-id="element-797" data-mp="dq7k083l4nkn">
        <div className="shell section" data-id="element-798" data-mp="hrsxnvny15vw">
          <SectionHeading align="split" eyebrow="Add-ons" title="Optional extras." lead="Add-on prices are quoted for your project and approved by you before any work begins." action={<PlaceholderTag data-id="element-800" data-mp="3q74et50bfn0t">Add-on pricing to be confirmed</PlaceholderTag>} data-id="element-799" data-mp="hqhy681j6kal" />
          <ul className="grid border-t border-line-strong md:grid-cols-2 2xl:grid-cols-3" data-id="element-801" data-mp="3q59qrhtqzesi">
            {ADDONS.map(a => <li key={a.name} className="flex items-start justify-between gap-6 border-b border-line py-6 md:pr-10" data-id="element-802" data-mp="2yggdnsyn6hn5">
                <div data-id="element-803" data-mp="32ggq5826kirk">
                  <h3 className="font-display text-[clamp(1.05rem,0.95rem+0.4vw,1.5rem)] font-medium tracking-tight" data-id="element-804" data-mp="32fyyypudkuqi">{a.name}</h3>
                  <p className="mt-1 text-muted" data-id="element-805" data-mp="2jakaf0viihxs">{a.note}</p>
                </div>
                <span className="shrink-0 whitespace-nowrap font-mono text-sm text-muted" data-id="element-806" data-mp="2efn2xydvsgn0">Quoted</span>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="shell section grid gap-10 lg:grid-cols-[0.8fr_1.2fr]" data-id="element-807" data-mp="2ob84xwiy9gwr">
        <SectionHeading eyebrow="Pricing FAQ" title="Pricing questions." data-id="element-808" data-mp="322d5r1y20f8o" />
        <FaqAccordion items={PRICING_FAQS} idPrefix="pricing-faq" data-id="element-809" data-mp="2x4jys6oxfh7c" />
      </section>

      <CtaBand source="pricing" data-id="element-810" data-mp="3c1khxlt1y3u7" />
    </>;
}